import React from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap, BadgeCheck, Sparkles, Users, Clock, Star,
  ChevronRight, Award, BookOpen, Target, Heart, Shield, Phone,
} from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';
import { dangerousSafeHtml } from '../utils/sanitizeHtml';

const SLUG_TO_PAGE_ID = {
  stp: 'staff-training-programme',
  sdph: 'skill-development-programme',
  lcp: 'liink-certification',
};

/**
 * Shared layout for the three info / programme pages:
 *  - Staff Training Programme (STP)
 *  - Skill Development Programme in Hospitality (SDPH)
 *  - Liink Certification for Property (LCP) Badge
 */

const PAGES = {
  stp: {
    abbr: 'STP',
    title: 'Staff Training Programme',
    tagline: 'Train. Certify. Elevate.',
    icon: GraduationCap,
    accent: '#FF0059',
    accentSoft: '#FFE5EE',
    hero: {
      heading: 'Equip your front-desk, housekeeping, and F&B staff with world-class training.',
      sub: 'Run by hospitality veterans, our STP modules turn average teams into 5★ teams in under 30 days.',
      stats: [
        { k: '12+', v: 'Modules' },
        { k: '3 weeks', v: 'Avg. duration' },
        { k: '4.8★', v: 'Trainer rating' },
        { k: '850+', v: 'Staff trained' },
      ],
    },
    pillars: [
      { icon: Users, title: 'Front-desk excellence', desc: 'Greeting protocol, check-in flow, complaint handling, upsell scripts.' },
      { icon: Heart, title: 'Hospitality mindset', desc: 'Guest empathy, body language, situational awareness, cultural sensitivity.' },
      { icon: Shield, title: 'Safety & hygiene', desc: 'FSSAI basics, room sanitation SOPs, emergency response drills.' },
      { icon: BookOpen, title: 'Operations playbook', desc: 'PMS / hotel admin handling, daily reporting, inventory & wastage control.' },
    ],
  },
  sdph: {
    abbr: 'SDPH',
    title: 'Skill Development Programme in Hospitality',
    tagline: 'Build a career. Build a craft.',
    icon: Target,
    accent: '#FFCB00',
    accentSoft: '#FFF9DB',
    hero: {
      heading: 'A 6-month immersive programme for aspiring hospitality professionals.',
      sub: 'Open to anyone in North India ready to commit — no prior degree required. Placement assistance included.',
      stats: [
        { k: '6 months', v: 'Full programme' },
        { k: '180+', v: 'Live hours' },
        { k: '92%', v: 'Placement rate' },
        { k: '₹0', v: 'Application fee' },
      ],
    },
    pillars: [
      { icon: Target, title: 'Hands-on rotations', desc: 'Real shifts at partner hotels — F&B, housekeeping, front-office, banquets.' },
      { icon: GraduationCap, title: 'Classroom theory', desc: 'Hospitality management fundamentals taught by industry mentors.' },
      { icon: Award, title: 'Soft skills & English', desc: 'Communication, etiquette, conversational English, conflict resolution.' },
      { icon: BadgeCheck, title: 'Certificate + placement', desc: 'Recognised industry certificate and guaranteed first-interview support.' },
    ],
  },
  lcp: {
    abbr: 'LCP',
    title: 'Liink Certification for Property',
    tagline: 'The LCP Badge — a guest-trust signal.',
    icon: BadgeCheck,
    accent: '#FF0059',
    accentSoft: '#FFE5EE',
    hero: {
      heading: 'Get the LCP Badge and stand out in every search result.',
      sub: 'Properties that meet our 38-point quality checklist earn the LCP Badge — visible to guests as a verified-quality marker.',
      stats: [
        { k: '38', v: 'Quality checks' },
        { k: '+27%', v: 'Booking lift' },
        { k: '4.6★', v: 'Avg. badge rating' },
        { k: 'Free', v: 'Audit & badge' },
      ],
    },
    pillars: [
      { icon: Sparkles, title: 'Cleanliness & hygiene', desc: 'Linen freshness, bathroom sanitation, kitchen audit, pest control records.' },
      { icon: Heart, title: 'Service quality', desc: 'Average response time, complaint resolution log, guest review score floor.' },
      { icon: Shield, title: 'Safety compliance', desc: 'Fire NOC, CCTV coverage, emergency exits, first-aid kit, ID verification.' },
      { icon: Star, title: 'Guest experience', desc: 'Welcome amenity, Wi-Fi speed test, accurate listing photos, accessibility.' },
    ],
  },
};

export const ProgrammePage = ({ slug }) => {
  const p = PAGES[slug];
  const { sections: c } = useSiteContent(SLUG_TO_PAGE_ID[slug] || slug);
  if (!p) return <div className="p-10 text-center">Programme not found.</div>;
  const Icon = p.icon;

  return (
    <div className="bg-white" data-testid={`programme-page-${slug}`}>
      {/* HERO */}
      <section className="relative overflow-hidden text-white bg-gradient-to-br from-[#0F1B2D] via-[#152540] to-[#0F1B2D]">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full opacity-25 blur-3xl" style={{ background: p.accent }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-xs font-bold mb-5">
            <Icon className="h-3.5 w-3.5" style={{ color: p.accent }} />
            {p.abbr} · localliink.com
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] max-w-3xl" data-testid={`${slug}-title`}>
            {c.hero_title || p.title}
          </h1>
          {c.hero_subtitle_html ? (
            <div
              className="mt-4 text-base sm:text-lg text-white/80 max-w-2xl prose prose-invert prose-sm prose-p:my-1"
              dangerouslySetInnerHTML={dangerousSafeHtml(c.hero_subtitle_html)}
              data-testid={`${slug}-subtitle`}
            />
          ) : (
            <>
              <p className="mt-4 text-base sm:text-lg text-white/80 max-w-2xl">{p.hero.heading}</p>
              <p className="mt-3 text-sm text-white/60 max-w-2xl">{p.hero.sub}</p>
            </>
          )}

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <a
              href="tel:+919988338381"
              className="inline-flex items-center gap-2 text-white font-bold px-6 py-3 rounded-full shadow-lg transition-colors hover:opacity-90"
              style={{ background: p.accent }}
              data-testid={`${slug}-cta-call`}
            >
              <Phone className="h-4 w-4" /> Call 99883 38381
            </a>
            <a
              href="mailto:programmes@localliink.com"
              className="inline-flex items-center gap-2 text-white/90 hover:text-white font-bold px-5 py-3 rounded-full border border-white/20 hover:border-white/40 transition-colors text-sm"
              data-testid={`${slug}-cta-email`}
            >
              programmes@localliink.com
            </a>
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-5 max-w-2xl" data-testid={`${slug}-stats`}>
            {p.hero.stats.map((s) => (
              <div key={s.v} className="text-left">
                <div className="text-2xl sm:text-3xl font-black" style={{ color: p.accent }}>{s.k}</div>
                <div className="text-[10px] sm:text-xs text-white/60 font-semibold uppercase tracking-wider mt-0.5">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid={`${slug}-pillars`}>
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-2" style={{ color: p.accent }}>What's inside</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-black">
            {p.tagline}
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {p.pillars.map((pl, i) => (
            <div
              key={pl.title}
              className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all"
              style={{ borderColor: i === 0 ? p.accent : undefined }}
              data-testid={`${slug}-pillar-${i}`}
            >
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: p.accentSoft }}>
                <pl.icon className="h-6 w-6" style={{ color: p.accent }} />
              </div>
              <h3 className="text-lg font-black text-black tracking-tight mb-1.5">{pl.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{pl.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY localliink.com */}
      <section className="bg-[#FAFAFA] py-16 lg:py-24" data-testid={`${slug}-why`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-2" style={{ color: p.accent }}>Powered by localliink.com</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-black">
            North India's hospitality network — at your service.
          </h2>
          <p className="text-gray-600 mt-4 text-base sm:text-lg">
            We work with 32+ partner hotels across Punjab, Haryana and Chandigarh. Your training, certification or audit will be performed on real properties — by mentors with 20+ years on the floor.
          </p>
          <div className="mt-7 inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-white border border-gray-200">
            <Clock className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-bold text-black">Next cohort opens in 2 weeks · Limited seats</span>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20" data-testid={`${slug}-final-cta`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-black leading-[1.1]" data-testid={`${slug}-cta-title`}>
            {c.cta_title || 'Ready to take the next step?'}
          </h2>
          <p className="text-gray-600 mt-4 text-base sm:text-lg max-w-2xl mx-auto">
            Drop us your details and our programme lead will reach out within 24 hours.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <a
              href="tel:+919988338381"
              className="inline-flex items-center gap-2 text-white font-bold px-7 py-4 rounded-full text-base shadow-xl transition-colors hover:opacity-90"
              style={{ background: p.accent }}
              data-testid={`${slug}-final-call`}
            >
              <Phone className="h-5 w-5" /> Call 99883 38381
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-black font-bold px-6 py-4 rounded-full border-2 border-black/10 hover:border-black transition-colors text-base"
            >
              Back to home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export const StaffTrainingProgrammePage = () => <ProgrammePage slug="stp" />;
export const SkillDevelopmentProgrammePage = () => <ProgrammePage slug="sdph" />;
export const LiinkCertificationPage = () => <ProgrammePage slug="lcp" />;
